import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { FeeService } from '../service/fee.service';
import { MemberPaymentPageResponse, MemberPaymentRecord, MemberSubgroupInfo } from '../interface/fee.interface';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../service/user.service';
import {FEE_PAYMENT_STATUS_DEFAULT_ICON, FEE_PAYMENT_STATUS_ICON, FEE_PAYMENT_STATUS_LABEL, FeePaymentStatus} from '../constants/fee-payment-status.constants';

@Component({
  selector: 'app-fee-payment-page',
  templateUrl: './fee-payment-page.component.html',
  styleUrls: ['./fee-payment-page.component.scss']
})
export class FeePaymentPageComponent implements OnInit {

  loading = true;
  error = false;
  data: MemberPaymentPageResponse | null = null;
  activeSubgroupId: number | null = null;
  private token = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly feeService: FeeService,
    private readonly translate: TranslateService,
    private readonly userService: UserService
  ) {}

  goBack(): void {
    this.router.navigate([this.userService.isLoggedIn() ? '/fees' : '/login']);
  }

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') ?? '';
    this.feeService.getPaymentPage(this.token).subscribe({
      next: (res) => {
        this.data = res;
        if (res.subgroups.length > 0) {
          this.activeSubgroupId = res.subgroups[0].id;
        }
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 404 || error.status === 400) {
          this.router.navigate(['/error-page']);
          return;
        }
        this.error = true;
        this.loading = false;
      }
    });
  }

  selectSubgroup(id: number): void {
    this.activeSubgroupId = id;
  }

  get activeSubgroup(): MemberSubgroupInfo | null {
    if (!this.data || this.activeSubgroupId === null) return null;
    return this.data.subgroups.find(s => s.id === this.activeSubgroupId) ?? null;
  }

  get filteredHistory(): MemberPaymentRecord[] {
    if (!this.data) return [];
    if (this.activeSubgroupId === null) return this.data.paymentHistory;
    return this.data.paymentHistory.filter(r => r.subgroupId === this.activeSubgroupId);
  }

  getSubgroupName(record: MemberPaymentRecord): string {
    if (!this.data) return '';
    const sg = this.data.subgroups.find(s => s.id === record.subgroupId);
    return sg ? sg.name : '';
  }

  frequencyLabel(value: string | undefined): string {
    if (!value) return '';
    const key = `fee.frequency_types.${value.toUpperCase()}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : value;
  }

  paymentStatusLabel(status: string | undefined): string {
    const key = (status || '').toUpperCase();
    if (key === 'PAID') return this.translate.instant('payment_status.paid');
    if (key === 'ENDED') return this.translate.instant('fee.ended');
    if (key === 'PENDING') return this.translate.instant('fee.not_paid');
    return status || '';
  }

  pay(record: MemberPaymentRecord): void {
    this.router.navigate(['/fee-payment', this.token, 'pay', record.id]);
  }

  statusIcon(record: MemberPaymentRecord): string {
    const status = this.normalizePaymentStatus(record.status);
    return FEE_PAYMENT_STATUS_ICON[status] ?? FEE_PAYMENT_STATUS_DEFAULT_ICON;
  }

  statusLabel(record: MemberPaymentRecord): string {
    const status = this.normalizePaymentStatus(record.status);
    return FEE_PAYMENT_STATUS_LABEL[status] ?? (record.status || '').toLowerCase().replace(/_/g, ' ');
  }

  trackByRecord(_: number, r: MemberPaymentRecord): number {
    return r.id;
  }

  trackBySubgroup(_: number, s: MemberSubgroupInfo): number {
    return s.id;
  }

  private normalizePaymentStatus(status: string | undefined): FeePaymentStatus {
    return (status || '').toUpperCase() as FeePaymentStatus;
  }
}
